import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { List, Plus, Trash2, ArrowLeft, Loader2, ShoppingCart, Pencil, Clock, Package, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getSavedLists, getSavedList, deleteSavedList, updateSavedList, createSavedList, applySavedList, getProductImageUrl } from '../../lib/api';
import { Toaster, toast } from 'sonner';

export default function SavedListsPage() {
  const { t, refreshCart, language } = useApp();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeList, setActiveList] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [applyModal, setApplyModal] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  useEffect(() => { fetchLists(); }, []);

  const fetchLists = async () => {
    try {
      const res = await getSavedLists();
      setLists(res.data);
    } catch { toast.error(t('savedLists.failedLoad')); }
    finally { setLoading(false); }
  };

  const openDetail = async (listId) => {
    setDetailLoading(true);
    setEditing(false);
    setApplyResult(null);
    try {
      const res = await getSavedList(listId);
      setActiveList(res.data);
      setEditName(res.data.name);
      setEditNotes(res.data.notes || '');
      setEditItems(res.data.items.map(it => ({ ...it })));
    } catch { toast.error(t('savedLists.failedLoad')); }
    finally { setDetailLoading(false); }
  };

  const handleDelete = async (listId) => {
    if (!window.confirm(t('savedLists.deleteConfirm'))) return;
    try {
      await deleteSavedList(listId);
      toast.success(t('savedLists.deleted'));
      setLists(prev => prev.filter(l => l.id !== listId));
      if (activeList?.id === listId) setActiveList(null);
    } catch { toast.error(t('savedLists.failedDelete')); }
  };

  const startEdit = () => {
    setEditing(true);
    setEditName(activeList.name);
    setEditNotes(activeList.notes || '');
    setEditItems(activeList.items.map(it => ({ ...it })));
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    const validItems = editItems.filter(it => it.qty > 0);
    if (validItems.length === 0) { toast.error(t('savedLists.noItems')); return; }
    setSaving(true);
    try {
      await updateSavedList(activeList.id, {
        name: editName.trim(),
        notes: editNotes.trim() || null,
        items: validItems.map(it => ({ product_id: it.product_id, qty: it.qty })),
      });
      toast.success(t('savedLists.updated'));
      setEditing(false);
      await openDetail(activeList.id);
      fetchLists();
    } catch { toast.error(t('savedLists.failedSave')); }
    finally { setSaving(false); }
  };

  const removeEditItem = (pid) => {
    setEditItems(prev => prev.filter(it => it.product_id !== pid));
  };

  const updateEditQty = (pid, qty) => {
    setEditItems(prev => prev.map(it => it.product_id === pid ? { ...it, qty: Math.max(1, qty) } : it));
  };

  const handleApply = async (mode) => {
    if (!applyModal) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await applySavedList(applyModal, { mode });
      toast.success(t('savedLists.applied'));
      setApplyResult(res.data);
      refreshCart();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('savedLists.failedApply'));
    } finally { setApplying(false); }
  };

  const gradeBadge = (grade) => {
    const g = grade?.toUpperCase() || '';
    if (g.includes('A')) return 'bg-[#10B981]';
    if (g.includes('B')) return 'bg-[#F59E0B]';
    if (g.includes('C')) return 'bg-[#EF4444]';
    return 'bg-gray-600';
  };

  // === Detail view ===
  if (activeList) {
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="saved-list-detail">
        <Toaster position="top-right" theme="dark" />
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <button onClick={() => { setActiveList(null); setEditing(false); }} className="text-white/60 hover:text-white" data-testid="back-to-lists-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              {editing ? (
                <input value={editName} onChange={e => setEditName(e.target.value)} className="text-2xl font-bold bg-transparent border-b border-white/20 focus:border-[#00D4FF] outline-none w-full" data-testid="edit-list-name" />
              ) : (
                <h1 className="text-2xl font-bold" data-testid="list-detail-name">{activeList.name}</h1>
              )}
              <p className="text-white/40 text-sm mt-1">
                {activeList.items.length} {t('savedLists.itemCount')} · {t('savedLists.lastUpdated')} {new Date(activeList.updated_at).toLocaleDateString(language === 'es' ? 'es' : 'en')}
              </p>
            </div>
            <div className="flex gap-2">
              {!editing ? (
                <>
                  <button onClick={startEdit} className="btn-secondary flex items-center gap-2 text-sm" data-testid="edit-list-btn">
                    <Pencil className="w-4 h-4" /> {t('common.edit')}
                  </button>
                  <button onClick={() => setApplyModal(activeList.id)} className="btn-primary flex items-center gap-2 text-sm" data-testid="apply-list-btn">
                    <ShoppingCart className="w-4 h-4" /> {t('savedLists.applyToCart')}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setEditing(false)} className="btn-secondary text-sm" data-testid="cancel-edit-btn">{t('common.cancel')}</button>
                  <button onClick={handleSaveEdit} disabled={saving} className="btn-primary flex items-center gap-2 text-sm" data-testid="save-edit-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t('common.save')}
                  </button>
                </>
              )}
            </div>
          </div>

          {editing && (
            <div className="mb-4">
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder={t('savedLists.notesPlaceholder')} rows={2} className="w-full px-4 py-2 bg-[#0A0A0A] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none" data-testid="edit-list-notes" />
            </div>
          )}

          {!editing && activeList.notes && (
            <div className="card-dark p-4 mb-4 text-white/60 text-sm">{activeList.notes}</div>
          )}

          <div className="card-dark overflow-hidden" data-testid="list-items-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0A0A0A] border-b border-white/10">
                    <th className="p-3 text-left text-xs font-semibold text-white/60 uppercase">SKU</th>
                    <th className="p-3 text-left text-xs font-semibold text-white/60 uppercase">{t('cart.brand')}</th>
                    <th className="p-3 text-left text-xs font-semibold text-white/60 uppercase">{t('cart.model')}</th>
                    <th className="p-3 text-center text-xs font-semibold text-white/60 uppercase">{t('cart.grade')}</th>
                    <th className="p-3 text-center text-xs font-semibold text-white/60 uppercase">{t('savedLists.available')}</th>
                    <th className="p-3 text-center text-xs font-semibold text-white/60 uppercase">{t('savedLists.qty')}</th>
                    <th className="p-3 text-right text-xs font-semibold text-white/60 uppercase">{t('cart.price')}</th>
                    {editing && <th className="p-3 w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {(editing ? editItems : activeList.items).map(item => (
                    <tr key={item.product_id} className={`border-b border-white/5 ${!item.available && !editing ? 'opacity-40' : ''}`} data-testid={`list-item-${item.product_id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {item.has_image && <img src={getProductImageUrl(item.product_id)} alt="" className="w-7 h-7 object-cover border border-white/10" />}
                          <span className="font-mono text-[#00D4FF] text-xs">{item.unique_id || '-'}</span>
                        </div>
                      </td>
                      <td className="p-3 text-white text-sm">{item.oem || '-'}</td>
                      <td className="p-3 text-white/80 text-sm">{item.model || '-'}</td>
                      <td className="p-3 text-center">
                        {item.grade && <span className={`px-1.5 py-0.5 text-[10px] font-bold text-white ${gradeBadge(item.grade)}`}>{item.grade}</span>}
                      </td>
                      <td className="p-3 text-center text-sm font-mono">
                        {item.available !== false ? (item.units_available ?? '-') : <span className="text-red-400 text-xs">{t('savedLists.unavailable')}</span>}
                      </td>
                      <td className="p-3 text-center">
                        {editing ? (
                          <input type="number" min="1" value={item.qty} onChange={e => updateEditQty(item.product_id, parseInt(e.target.value) || 1)} className="w-16 px-2 py-1 bg-transparent border border-white/10 text-center text-white font-mono text-sm focus:border-[#00D4FF] outline-none" data-testid={`edit-qty-${item.product_id}`} />
                        ) : (
                          <span className="font-mono text-sm">{item.qty}</span>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono text-sm text-[#00D4FF]">
                        ${(item.sale_price || 0).toLocaleString()}
                      </td>
                      {editing && (
                        <td className="p-3">
                          <button onClick={() => removeEditItem(item.product_id)} className="text-white/30 hover:text-red-400" data-testid={`remove-item-${item.product_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Apply Modal */}
        {applyModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="apply-modal-overlay">
            <div className="bg-[#111] border border-white/10 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" data-testid="apply-modal">
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-lg font-semibold">{t('savedLists.applyMode')}</h2>
                <button onClick={() => { setApplyModal(null); setApplyResult(null); }} className="text-white/40 hover:text-white" data-testid="close-apply-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {!applyResult ? (
                  <>
                    <button onClick={() => handleApply('replace')} disabled={applying} className="w-full p-4 border border-white/10 hover:border-[#00D4FF]/40 text-left transition-colors" data-testid="apply-replace-btn">
                      <div className="font-semibold text-[#00D4FF]">{t('savedLists.replace')}</div>
                      <div className="text-white/50 text-sm mt-1">{t('savedLists.replaceDesc')}</div>
                    </button>
                    <button onClick={() => handleApply('merge')} disabled={applying} className="w-full p-4 border border-white/10 hover:border-[#00D4FF]/40 text-left transition-colors" data-testid="apply-merge-btn">
                      <div className="font-semibold text-[#00D4FF]">{t('savedLists.merge')}</div>
                      <div className="text-white/50 text-sm mt-1">{t('savedLists.mergeDesc')}</div>
                    </button>
                    {applying && <div className="text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#00D4FF]" /></div>}
                  </>
                ) : (
                  <div className="space-y-4" data-testid="apply-results">
                    <div className="flex items-center gap-2 text-[#10B981]">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">{t('savedLists.resultsTitle')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#10B981]/10 border border-[#10B981]/20 p-3 text-center">
                        <div className="text-xl font-bold text-[#10B981] font-mono">{applyResult.added_count}</div>
                        <div className="text-white/50 text-xs">{t('savedLists.addedToCart')}</div>
                      </div>
                      <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 p-3 text-center">
                        <div className="text-xl font-bold text-[#F59E0B] font-mono">{applyResult.updated_count}</div>
                        <div className="text-white/50 text-xs">{t('savedLists.alreadyInCart')}</div>
                      </div>
                    </div>
                    {applyResult.skipped_items?.length > 0 && (
                      <div className="bg-[#EF4444]/10 border border-[#EF4444]/20 p-3">
                        <div className="flex items-center gap-2 text-[#EF4444] text-sm font-medium mb-2">
                          <AlertTriangle className="w-4 h-4" /> {t('savedLists.skippedItems')} ({applyResult.skipped_items.length})
                        </div>
                        {applyResult.skipped_items.map((si, i) => (
                          <div key={i} className="text-xs text-white/50 py-0.5">{si.unique_id || si.product_id}: {si.reason.replace(/_/g, ' ')}</div>
                        ))}
                      </div>
                    )}
                    {applyResult.adjusted_items?.length > 0 && (
                      <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 p-3">
                        <div className="text-[#F59E0B] text-sm font-medium mb-2">{t('savedLists.adjustedItems')} ({applyResult.adjusted_items.length})</div>
                        {applyResult.adjusted_items.map((ai, i) => (
                          <div key={i} className="text-xs text-white/50 py-0.5">{ai.unique_id}: {t('savedLists.requested')} {ai.requested_qty} → {t('savedLists.appliedQty')} {ai.applied_qty}</div>
                        ))}
                      </div>
                    )}
                    <button onClick={() => navigate('/buyer/cart')} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="go-to-cart-btn">
                      <ShoppingCart className="w-4 h-4" /> {t('buyerDash.viewCart')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // === List view ===
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="saved-lists-page">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/buyer" className="text-white/60 hover:text-white" data-testid="back-to-dashboard-link">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <List className="w-7 h-7 text-[#00D4FF]" />
                {t('savedLists.title')}
              </h1>
              <p className="text-white/50 text-sm mt-1">{lists.length} {t('savedLists.itemCount')}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="card-dark p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00D4FF]" /></div>
        ) : lists.length === 0 ? (
          <div className="card-dark p-12 text-center" data-testid="empty-saved-lists">
            <Package className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('savedLists.empty')}</h2>
            <p className="text-white/50 mb-6">{t('savedLists.emptyMsg')}</p>
            <Link to="/products" className="btn-primary inline-block" data-testid="browse-products-link">{t('buyerDash.browseInventory')}</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {lists.map(list => (
              <div key={list.id} className="card-dark p-5" data-testid={`saved-list-row-${list.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">{list.name}</div>
                    <div className="text-white/40 text-xs mt-1 flex items-center gap-3">
                      <span>{list.item_count} {t('savedLists.itemCount')}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(list.updated_at).toLocaleDateString(language === 'es' ? 'es' : 'en')}</span>
                    </div>
                    {list.notes && <div className="text-white/30 text-xs mt-1 truncate">{list.notes}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => openDetail(list.id)} className="px-3 py-1.5 text-xs bg-white/5 hover:bg-[#00D4FF]/10 text-white/70 hover:text-[#00D4FF] transition-colors" data-testid={`view-list-${list.id}`}>
                      {t('savedLists.viewEdit')}
                    </button>
                    <button onClick={() => setApplyModal(list.id)} className="px-3 py-1.5 text-xs bg-[#00D4FF]/10 text-[#00D4FF] hover:bg-[#00D4FF]/20 transition-colors flex items-center gap-1" data-testid={`apply-list-${list.id}`}>
                      <ShoppingCart className="w-3 h-3" /> {t('savedLists.applyToCart')}
                    </button>
                    <button onClick={() => handleDelete(list.id)} className="p-1.5 text-white/30 hover:text-red-400 transition-colors" data-testid={`delete-list-${list.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Apply Modal (also accessible from list view) */}
      {applyModal && !activeList && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="apply-modal-overlay">
          <div className="bg-[#111] border border-white/10 w-full max-w-md mx-4" data-testid="apply-modal">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-lg font-semibold">{t('savedLists.applyMode')}</h2>
              <button onClick={() => { setApplyModal(null); setApplyResult(null); }} className="text-white/40 hover:text-white" data-testid="close-apply-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {!applyResult ? (
                <>
                  <button onClick={() => handleApply('replace')} disabled={applying} className="w-full p-4 border border-white/10 hover:border-[#00D4FF]/40 text-left transition-colors" data-testid="apply-replace-btn">
                    <div className="font-semibold text-[#00D4FF]">{t('savedLists.replace')}</div>
                    <div className="text-white/50 text-sm mt-1">{t('savedLists.replaceDesc')}</div>
                  </button>
                  <button onClick={() => handleApply('merge')} disabled={applying} className="w-full p-4 border border-white/10 hover:border-[#00D4FF]/40 text-left transition-colors" data-testid="apply-merge-btn">
                    <div className="font-semibold text-[#00D4FF]">{t('savedLists.merge')}</div>
                    <div className="text-white/50 text-sm mt-1">{t('savedLists.mergeDesc')}</div>
                  </button>
                  {applying && <div className="text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#00D4FF]" /></div>}
                </>
              ) : (
                <div className="space-y-4" data-testid="apply-results">
                  <div className="flex items-center gap-2 text-[#10B981]">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">{t('savedLists.resultsTitle')}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#10B981]/10 border border-[#10B981]/20 p-3 text-center">
                      <div className="text-xl font-bold text-[#10B981] font-mono">{applyResult.added_count}</div>
                      <div className="text-white/50 text-xs">{t('savedLists.addedToCart')}</div>
                    </div>
                    <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 p-3 text-center">
                      <div className="text-xl font-bold text-[#F59E0B] font-mono">{applyResult.updated_count}</div>
                      <div className="text-white/50 text-xs">{t('savedLists.alreadyInCart')}</div>
                    </div>
                  </div>
                  {applyResult.skipped_items?.length > 0 && (
                    <div className="bg-[#EF4444]/10 border border-[#EF4444]/20 p-3">
                      <div className="flex items-center gap-2 text-[#EF4444] text-sm font-medium mb-2"><AlertTriangle className="w-4 h-4" /> {t('savedLists.skippedItems')}</div>
                      {applyResult.skipped_items.map((si, i) => (
                        <div key={i} className="text-xs text-white/50">{si.unique_id || si.product_id}: {si.reason.replace(/_/g, ' ')}</div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => navigate('/buyer/cart')} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="go-to-cart-btn">
                    <ShoppingCart className="w-4 h-4" /> {t('buyerDash.viewCart')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
